export interface TechCard {
  key: string;
  title: string;
  icon: string;
  description: string;
}

export const technologyCards: TechCard[] = [
  {
    key: "powertrain",
    title: "Electric Motor / Powertrain",
    icon: "ri-flashlight-line",
    description:
      "The electric motor replaces the internal combustion drive. Motor selection is matched to the vehicle, duty cycle and application rather than a fixed universal specification.",
  },
  {
    key: "battery",
    title: "Battery System",
    icon: "ri-battery-charge-line",
    description:
      "The battery system provides onboard energy storage. Capacity and configuration depend on the vehicle, weight, expected usage and engineering requirements.",
  },
  {
    key: "bms",
    title: "Battery Management System",
    icon: "ri-pulse-line",
    description:
      "The BMS monitors and manages battery operation, supporting safe and reliable use across the working range of the pack.",
  },
  {
    key: "vcu",
    title: "Vehicle Control Systems",
    icon: "ri-cpu-line",
    description:
      "Control systems coordinate the powertrain, energy flow and driver inputs so the vehicle behaves predictably after conversion.",
  },
  {
    key: "power",
    title: "Power Electronics",
    icon: "ri-plug-line",
    description:
      "Controllers, converters and related power electronics manage electrical energy between the battery, motor and auxiliary systems.",
  },
  {
    key: "thermal",
    title: "Thermal Management",
    icon: "ri-temp-cold-line",
    description:
      "Thermal behaviour is considered for the battery and key electrical components as part of the retrofit engineering process.",
  },
  {
    key: "safety",
    title: "Safety Systems",
    icon: "ri-shield-check-line",
    description:
      "Electrical protection, isolation and monitoring concepts are designed in so the retrofit meets engineering safety considerations.",
  },
  {
    key: "connected",
    title: "Connected EV Technology",
    icon: "ri-signal-tower-line",
    description:
      "Connectivity concepts aim to support vehicle monitoring, charging information and service visibility across the ecosystem.",
  },
  {
    key: "charging",
    title: "Charging Ecosystem",
    icon: "ri-charging-pile-line",
    description:
      "Charging compatibility is reviewed as part of the retrofit so the vehicle fits into an everyday charging routine.",
  },
];

export interface TechArchitectureStep {
  key: string;
  label: string;
  icon: string;
  description: string;
}

export const architectureFlow: TechArchitectureStep[] = [
  {
    key: "vehicle",
    label: "Vehicle",
    icon: "ri-car-line",
    description: "A suitable existing vehicle assessed for retrofit.",
  },
  {
    key: "powertrain",
    label: "Electric Powertrain",
    icon: "ri-flashlight-line",
    description: "The combustion drive replaced by an electric motor.",
  },
  {
    key: "controller",
    label: "Controller",
    icon: "ri-cpu-line",
    description: "Motor control and power delivery management.",
  },
  {
    key: "battery",
    label: "Battery + BMS",
    icon: "ri-battery-charge-line",
    description: "Onboard energy storage with continuous management.",
  },
  {
    key: "charging",
    label: "Charging",
    icon: "ri-charging-pile-line",
    description: "Charging compatibility within the EV ecosystem.",
  },
  {
    key: "connected",
    label: "Connected Services",
    icon: "ri-signal-tower-line",
    description: "Monitoring, service and fleet visibility concepts.",
  },
];

export interface TechDetail {
  key: string;
  title: string;
  icon: string;
  points: string[];
}

export const technologyDetails: TechDetail[] = [
  {
    key: "architecture",
    title: "Retrofit Architecture",
    icon: "ri-layout-masonry-line",
    points: [
      "Vehicle assessed as the starting point of the architecture",
      "Components integrated around the existing structure",
      "Layout decided after engineering assessment",
    ],
  },
  {
    key: "powertrain",
    title: "Electric Powertrain",
    icon: "ri-flashlight-line",
    points: [
      "Motor and drive matched to the vehicle and duty cycle",
      "Transmission interface reviewed during engineering",
      "Specification depends on vehicle and configuration",
    ],
  },
  {
    key: "battery",
    title: "Battery Pack",
    icon: "ri-battery-charge-line",
    points: [
      "Pack selection based on weight, usage and requirement",
      "Mounting and protection considered as part of integration",
      "Capacity is configuration dependent",
    ],
  },
  {
    key: "bms",
    title: "BMS",
    icon: "ri-pulse-line",
    points: [
      "Monitors and manages battery operation",
      "Supports safe operation across the working range",
      "Works alongside protection and monitoring systems",
    ],
  },
  {
    key: "controller",
    title: "Controller",
    icon: "ri-cpu-line",
    points: [
      "Manages energy flow between battery and motor",
      "Coordinates driver inputs with the powertrain",
      "Configuration set for the specific retrofit",
    ],
  },
  {
    key: "dcdc",
    title: "DC/DC",
    icon: "ri-plug-line",
    points: [
      "Supplies low-voltage auxiliary systems",
      "Supports onboard electrical loads",
      "Integrated within the retrofit electrical architecture",
    ],
  },
  {
    key: "charging",
    title: "Charging",
    icon: "ri-charging-pile-line",
    points: [
      "Charging compatibility reviewed during engineering",
      "Designed for everyday charging routines",
      "Aligned with the wider charging ecosystem",
    ],
  },
  {
    key: "connectivity",
    title: "Connectivity",
    icon: "ri-signal-tower-line",
    points: [
      "Monitoring concepts for the vehicle and charging",
      "Service and usage visibility possibilities",
      "Feature availability confirmed per configuration",
    ],
  },
  {
    key: "safety",
    title: "Safety",
    icon: "ri-shield-check-line",
    points: [
      "Electrical protection and isolation approach",
      "Battery safety and thermal considerations",
      "Testing as part of the process",
    ],
  },
  {
    key: "diagnostics",
    title: "Diagnostics",
    icon: "ri-stethoscope-line",
    points: [
      "System-level checks during engineering",
      "Support for service and maintenance activity",
      "Approach defined for the retrofit configuration",
    ],
  },
  {
    key: "service",
    title: "Service & Maintenance",
    icon: "ri-tools-line",
    points: [
      "Serviceability considered during design",
      "Maintenance expectations confirmed after assessment",
      "Applicable approvals depend on vehicle category and regulations",
    ],
  },
];